import React, {
    useRef,
    MutableRefObject,
    useCallback,
    useEffect,
} from 'react';
import classnames from 'classnames/bind';

import { notify } from '@NOTIFICATION/Notificator';

import BaseModal from '@MODALS/BaseModal';
import BasicButton from '@COMPONENTS/COMMON/buttons/BasicButton';
import IntroductionTextEditor from './components/IntroductionTextEditor/IntroductionTextEditor.component';

import styles from './IntroductionTextModal.module.scss';

const cx: CX = classnames.bind(styles);

interface IntroductionTextModalConfig {
    title: string;
    subtitle: string;
    successMessage: string;
    errorMessage: string;
}

interface IntroductionTextModalProps {
    isOpen: boolean;
    onClose: () => void;
    initialValue: string;
    canEdit: boolean;
    config: IntroductionTextModalConfig;
    onSave: (content: string) => void;
    isSaving?: boolean;
    saveError?: boolean;
    saveSuccess?: boolean;
}

function IntroductionTextModal({
    isOpen,
    onClose,
    initialValue,
    canEdit,
    config,
    onSave,
    isSaving = false,
    saveError = false,
    saveSuccess = false,
}: IntroductionTextModalProps) {
    const editorRef: MutableRefObject<string> = useRef('');

    const handleClose = useCallback(() => {
        onClose();
    }, [onClose]);

    function getEditorContent() {
        return editorRef.current;
    }

    const handleSave = useCallback(async () => {
        if (!canEdit) return;
        
        const content = getEditorContent();
        onSave(content);
    }, [canEdit, onSave]);

    const handleEditorChange = useCallback((content: string) => {
        editorRef.current = content;
    }, []);

    // Handle success/error states
    useEffect(() => {
        if (saveSuccess) {
            notify.success(config.successMessage, {
                toastId: 'save-introduction-text',
            });
            handleClose();
        }
    }, [saveSuccess, handleClose, config.successMessage]);

    useEffect(() => {
        if (saveError) {
            notify.error(config.errorMessage, {
                toastId: 'save-introduction-text-error',
            });
        }
    }, [saveError, config.errorMessage]);

    if (!isOpen) return null;

    return (
        <BaseModal 
            style={{
                width: '90%',
                maxWidth: '1200px',
                height: '90%',
                maxHeight: '800px',
            }}
            onClose={handleClose}
        >
            <div className={cx('introduction-text-modal')}>
                <div className={cx('header')}>
                    <h2 className={cx('title')}>
                        {config.title}
                    </h2>
                    <p className={cx('subtitle')}>
                        {config.subtitle}
                    </p>
                </div>

                <div className={cx('content')}>
                    <div className={cx('editor-wrapper')}>
                        <IntroductionTextEditor
                            initialValue={initialValue}
                            onEditorChange={handleEditorChange}
                        />
                    </div>
                </div>

                <div className={cx('footer')}>
                    <BasicButton
                        variant="neutral"
                        size="medium"
                        title="Cancel"
                        onClick={handleClose}
                        style={{ flex: 1 }}
                    />
                    <BasicButton
                        variant="success"
                        size="medium"
                        title={isSaving ? 'Saving...' : 'Save Introduction Text'}
                        onClick={handleSave}
                        isProcessing={isSaving}
                        locked={!canEdit}
                        style={{ flex: 2 }}
                    />
                </div>
            </div>
        </BaseModal>
    );
}

IntroductionTextModal.defaultProps = {
    isSaving: false,
    saveError: false,
    saveSuccess: false,
};

export default IntroductionTextModal; 